import React, { useCallback, useEffect, useRef } from 'react';

import qs from 'query-string';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import Lottie from 'lottie-react';
import { LoadTableResult, useOrder } from '../../hooks/order';
import { useToast } from '../../hooks/toast';

import Container, { AnimationSlot } from './styles';
import { useAuth } from '../../hooks/auth';
import objectToQueryParams from '../../utils/uri';
import { useCompany } from '../../hooks/company';
import useAnimation from '../../hooks/animation';

/** A table load and the effect runs currently waiting for it. */
interface ITableLoad {
  search: string;
  promise: Promise<LoadTableResult>;
  /** Effect runs subscribed right now. `loadTable` reads it synchronously through `isStillWanted`. */
  subscribers: number;
  /** Set once no run picked the load up again after a cleanup. An abandoned load is never reused. */
  abandoned: boolean;
}

interface ILoadingPageProps {
  message?: string;
  /**
   * Shows the loading screen without loading the table. For a copy rendered while something else
   * loads: `CompanyStripeProvider` shows one while the company loads, even on `/hash`, and then swaps
   * it for the routes, whose `/hash` instance starts the session. Two instances loading would start
   * two sessions and send two `/hash` requests for one QR read.
   */
  skipTableLoad?: boolean;
}
const LoadingPage: React.FC<ILoadingPageProps> = ({
  message = 'Carregando, aguarde...',
  skipTableLoad = false,
}) => {
  const [searchParams] = useSearchParams();
  const { company, loading } = useCompany();
  const animationData = useAnimation(company?.loadingAnimation, !loading);
  const navigate = useNavigate();
  const location = useLocation();
  const token = searchParams.get('token');
  const { beginTableSession, loadTable } = useOrder();
  const { addToast } = useToast();
  const { customer, confirmToken } = useAuth();

  // The table load in flight, one per `location.search`. It lives in a ref so every run of the effect
  // below can pick the same load up again instead of starting another one.
  const tableLoadRef = useRef<ITableLoad | null>(null);

  const loadUser = useCallback(
    async (token: string) => {
      try {
        if (typeof token === 'string') {
          await confirmToken(token);
        }
      } catch (error) {
        console.error('error', error);
      }
    },
    [confirmToken],
  );

  // The only place that loads the table. One logical QR read starts one table session and one load, no
  // matter how many times this effect runs: StrictMode runs it twice (setup, cleanup, setup), and a new
  // `navigate` or `loadTable` identity runs it again. A run for the same search does not start anything;
  // it subscribes to the load already in flight.
  //
  // Every run subscribes with its own `active` flag and its cleanup turns it off, so only the latest
  // subscription reacts. That is what makes the second StrictMode run conclude the load the first one
  // started, and what keeps a load for a previous search, or one still pending when the page unmounts,
  // from showing a toast or navigating.
  //
  // The flag only covers this page. The provider writes the table and applies the 401/404/5xx redirects
  // itself, and leaving `/hash` does not change the table session, so the provider also asks the page,
  // right before each write or redirect, whether anyone still waits: `subscribers > 0`. It has to be
  // that synchronous count, never a flag set later: the continuation of the request can already be
  // queued when the page is left (the answer and the navigation in the same tick), and it would run
  // before any deferred step. StrictMode is safe with it: its cleanup is followed at once by the setup
  // that subscribes again, with no await in between.
  //
  // `abandoned` only decides reuse. It is set in a microtask, when no run subscribed again after a
  // cleanup (leaving `/hash`, unmounting, another search), so StrictMode's second run still picks the
  // same load up while coming back to the same search later is a new read.
  useEffect(() => {
    if (skipTableLoad || location.pathname !== '/hash' || !location.search) return undefined;

    const startTableLoad = (): ITableLoad => {
      const parsed = qs.parse(location.search);
      const refreshSession = parsed.refreshToken !== 'false';

      // Before the load, never after: the previous session is gone even if this load fails.
      if (refreshSession) beginTableSession();

      // `loadTable` only asks after its request, by which time this run has subscribed below.
      const load: ITableLoad = {
        search: location.search,
        promise: loadTable(parsed.token?.toString(), refreshSession, () => load.subscribers > 0),
        subscribers: 0,
        abandoned: false,
      };

      tableLoadRef.current = load;

      return load;
    };

    const previous = tableLoadRef.current;

    const operation =
      previous && previous.search === location.search && !previous.abandoned
        ? previous
        : startTableLoad();

    operation.subscribers += 1;
    let active = true;

    operation.promise.then((result) => {
      if (!active || tableLoadRef.current !== operation) return;

      if (result === 'loaded') {
        navigate('/');
      } else if (result === 'failed') {
        // Never open the menu for a table that did not load.
        addToast({
          type: 'error',
          description: 'Não foi possível carregar a mesa. Leia o QR Code novamente.',
        });
        navigate('/table', { replace: true, state: { expired: true } });
      }
      // 'redirected': the page already went where it always did on 401, 404 and 5xx.
      // 'stale': the load belongs to a session that was replaced, or it was abandoned; nothing to do.
    });

    return () => {
      active = false;
      operation.subscribers -= 1;

      Promise.resolve().then(() => {
        if (operation.subscribers === 0) operation.abandoned = true;
      });
    };
  }, [
    skipTableLoad,
    location.pathname,
    location.search,
    beginTableSession,
    loadTable,
    navigate,
    addToast,
  ]);

  useEffect(() => {
    if (location.search && location.pathname !== '/hash' && typeof token === 'string') {
      loadUser(token);
    }
  }, [navigate, confirmToken, loadUser, token, location]);

  useEffect(() => {
    const params = qs.parse(location.search);
    const { redirect } = params;

    if (customer?.firstAccess) {
      navigate('me', {
        state: {
          subroute: 'user',
        },
      });
    } else if (customer && typeof redirect === 'string') {
      delete params.subdomain;
      delete params.redirect;
      delete params.token;

      const queryParams = objectToQueryParams(params);

      navigate(`/${redirect.replace(/\/+/, '')}?${queryParams}`);
    }
  }, [customer, location, navigate]);
  return (
    <Container>
      <div>
        <AnimationSlot>
          {!loading && animationData ? (
            <Lottie
              animationData={animationData}
              loop
              autoPlay
              style={{ width: 300, height: 300 }}
            />
          ) : null}
        </AnimationSlot>

        <span>{message}</span>
      </div>
    </Container>
  );
};

export default LoadingPage;
